import React, { createContext } from 'react';
import { MessageBundle } from '@amzn/arb-tools';
import { formatLocale, mbmInitializer } from './mbm';
import { useInternalChromeConfigContext } from 'src/providers/InternalChromeConfigContextProvider';
import { isFromTrim } from 'src/commons/utils';

type UseLocalizationBundleHook<T> = () => T;

const defaultLocalizationBundle = {
  getMessage: () => '',
  formatMessage: () => '',
};
const AccountDropdownLocalizationBundleContext = createContext<MessageBundle>(
  defaultLocalizationBundle,
);

const SidenavLocalizationBundleContext = createContext<MessageBundle>(
  defaultLocalizationBundle,
);

const PivotNavLocalizationBundleContext = createContext<MessageBundle>(
  defaultLocalizationBundle,
);

const PageHeaderLocalizationBundleContext = createContext<MessageBundle>(
  defaultLocalizationBundle,
);

const FooterLocalizationBundleContext = createContext<MessageBundle>(
  defaultLocalizationBundle,
);

const HeaderLocalizationBundleContext = createContext<MessageBundle>(
  defaultLocalizationBundle,
);

const createUseLocalizationBundleHook: <T>(
  localizationContext: React.Context<T>,
) => UseLocalizationBundleHook<T> = (localizationContext) => () => {
  const context = React.useContext(localizationContext);
  if (!context) {
    throw new Error(
      `${localizationContext.displayName} not found.  ${localizationContext.displayName} must be used with the corresponding Provider.`,
    );
  }

  return context;
};

const createBundleProvider: (
  LocalizationBundleContext: React.Context<MessageBundle>,
  bundleName: string,
) => React.FC = (LocalizationBundleContext, bundleName) => {
  const ReturnComponent: React.FC = ({ children }) => {
    const [bundle, setBundle] = React.useState<MessageBundle>();
    const { config: internalChromeConfig } = useInternalChromeConfigContext();
    React.useEffect(() => {
      const getBundle = async () => {
        const mbm = mbmInitializer(formatLocale(internalChromeConfig.locale))();
        const loadedBundle = await mbm.getBundle(bundleName);
        setBundle(loadedBundle);
      };
      getBundle();
    }, []);

    if (!bundle && !isFromTrim()) {
      return null;
    }

    const wrappedBundle: MessageBundle = {
      getMessage: (messageKey: string): string => {
        try {
          return bundle?.getMessage(messageKey) ?? '';
        } catch {
          return '';
        }
      },
      formatMessage: (
        messageKey: string,
        args: Record<string, unknown>,
      ): string => {
        try {
          return bundle?.formatMessage(messageKey, args) ?? '';
        } catch {
          // log error
          return '';
        }
      },
    };

    return (
      <LocalizationBundleContext.Provider value={wrappedBundle}>
        {children}
      </LocalizationBundleContext.Provider>
    );
  };
  ReturnComponent.displayName = `${LocalizationBundleContext.displayName}BundleProvider`;
  return ReturnComponent;
};

export const useAccountDropdownBundle = createUseLocalizationBundleHook(
  AccountDropdownLocalizationBundleContext,
);

export const AccountDropdownBundleProvider = createBundleProvider(
  AccountDropdownLocalizationBundleContext,
  'components.accountSwitcher',
);

export const useSidenavBundle = createUseLocalizationBundleHook(
  SidenavLocalizationBundleContext,
);

export const SidenavBundleProvider = createBundleProvider(
  SidenavLocalizationBundleContext,
  'components.sidenav',
);

export const usePageHeaderBundle = createUseLocalizationBundleHook(
  PageHeaderLocalizationBundleContext,
);

export const PageHeaderBundleProvider = createBundleProvider(
  PageHeaderLocalizationBundleContext,
  'components.pageHeader',
);

export const useFooterBundle = createUseLocalizationBundleHook(
  FooterLocalizationBundleContext,
);

export const FooterBundleProvider = createBundleProvider(
  FooterLocalizationBundleContext,
  'components.footer',
);

export const usePivotNavBundle = createUseLocalizationBundleHook(
  PivotNavLocalizationBundleContext,
);

export const PivotNavBundleProvider = createBundleProvider(
  PivotNavLocalizationBundleContext,
  'components.pivotNav',
);

export const useHeaderBundle = createUseLocalizationBundleHook(
  HeaderLocalizationBundleContext,
);

export const HeaderBundleProvider = createBundleProvider(
  HeaderLocalizationBundleContext,
  'components.header',
);
